import React, { useCallback, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useResetOnBfcacheRestore } from '../../hooks/useResetOnBfcacheRestore';
import { authColors, shadows } from '../../styles/colors';
import OtpVerification from './OtpVerification';
import { isEmailAlreadyRegisteredError } from '../../utils/authErrors';
import { trackUmamiEvent } from '../../utils/umami';
import { trackMetaCompleteRegistration } from '../../utils/metaPixel';
import { trackOpenAICompleteRegistration } from '../../utils/openaiPixel';

interface SignUpFormProps {
  onSwitchToLogin?: () => void;
  initialEmail?: string;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onSwitchToLogin, initialEmail }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: '',
    email: initialEmail || '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOtp, setShowOtp] = useState(false);
  const { signUp, signInWithGoogle } = useAuth();

  useResetOnBfcacheRestore(useCallback(() => setGoogleLoading(false), []));

  const trackSignupError = (reason: string) => {
    trackUmamiEvent('signup_error', { reason });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Email validation (trim and lowercase)
    const trimmedEmail = formData.email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
      setError(t('auth.invalidEmail'));
      trackSignupError('validation');
      setLoading(false);
      return;
    }

    const trimmedPassword = formData.password.trim();
    
    if (trimmedPassword.length === 0) {
      setError(t('auth.passwordNotWhitespace'));
      trackSignupError('validation');
      setLoading(false);
      return;
    }

    if (trimmedPassword.length < 8) {
      setError(t('auth.minPasswordLength'));
      trackSignupError('validation');
      setLoading(false);
      return;
    }

    if (formData.password.length > 128) {
      setError(t('auth.passwordTooLong'));
      trackSignupError('validation');
      setLoading(false);
      return;
    }

    if (!/[A-Z]/.test(trimmedPassword)) {
      setError(t('auth.passwordNoUppercase'));
      trackSignupError('validation');
      setLoading(false);
      return;
    }

    if (!/[a-z]/.test(trimmedPassword)) {
      setError(t('auth.passwordNoLowercase'));
      trackSignupError('validation');
      setLoading(false);
      return;
    }

    if (!/[0-9]/.test(trimmedPassword)) {
      setError(t('auth.passwordNoDigit'));
      trackSignupError('validation');
      setLoading(false);
      return;
    }

    try {
      // Sign up with password - Supabase will send OTP if configured in dashboard
      const { error } = await signUp(trimmedEmail, trimmedPassword, {
        name: formData.name.trim(),
      });
      
      if (error) {
        // Enhanced error handling for better UX
        const errorMessage = error.message || '';
        const errorCode = error.status || error.code || '';

        const isEmailExists = isEmailAlreadyRegisteredError(error);

        const isInvalidEmail = 
          errorMessage.toLowerCase().includes('invalid format') ||
          errorMessage.toLowerCase().includes('unable to validate email') ||
          errorMessage.toLowerCase().includes('invalid email') ||
          errorCode === 'invalid_email';
        
        // Check for rate limiting (e.g., "For security purposes, you can only request this after 30 seconds")
        const isRateLimited = 
          error.status === 429 || 
          errorMessage.toLowerCase().includes('too many requests') ||
          errorMessage.toLowerCase().includes('you can only request this after');
        
        if (isEmailExists) {
          setError(t('auth.emailAlreadyExistsMessage'));
          trackSignupError('email_exists');
        } else if (isInvalidEmail) {
          setError(t('auth.invalidEmail'));
          trackSignupError('invalid_email');
        } else if (isRateLimited) {
          // Extract seconds and show user-friendly translated message
          const waitTimeMatch = errorMessage.match(/(\d+)\s*seconds?/i);
          const waitSeconds = waitTimeMatch ? parseInt(waitTimeMatch[1], 10) : 30;
          setError(t('auth.signUpRateLimited', { seconds: waitSeconds }));
          trackSignupError('rate_limited');
        } else {
          // Show the actual error message from Supabase for debugging
          setError(errorMessage || t('auth.signUpError'));
          trackSignupError('other');
        }
        setLoading(false);
      } else {
        // OTP sent successfully - show OTP verification screen
        // Password is already set in the account via signUp
        setShowOtp(true);
        setError(null);
      }
    } catch (err) {
      setError(t('auth.unexpectedError'));
      trackSignupError('other');
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOtpVerified = () => {
    trackUmamiEvent('signup_completed', { method: 'email' });
    trackMetaCompleteRegistration('email');
    trackOpenAICompleteRegistration('email');
    // OTP verified successfully - password is already set via signUp
    // SignUpPage will handle redirect via useEffect when user state updates
    // (including checkout redirect if pending)
    // Do nothing here - let SignUpPage handle all redirects
  };

  // Show OTP verification screen
  if (showOtp) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <OtpVerification
          email={formData.email}
          userData={{ name: formData.name.trim() }}
          onVerified={handleOtpVerified}
          onBack={() => {
            setShowOtp(false);
            setLoading(false);
            setError(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <div className="text-center">
        <div className={`mx-auto h-12 w-12 ${authColors.iconGradient} rounded-full flex items-center justify-center mb-4`}>
          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {t('auth.signUpTitle')}
        </h2>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <button
          type="button"
          onClick={async () => {
            setGoogleLoading(true);
            setError(null);
            trackUmamiEvent('signup_google_click');
            const { error: err } = await signInWithGoogle();
            if (err) {
              setError(err.message || t('auth.signUpError'));
              trackSignupError('google');
              setGoogleLoading(false);
            }
          }}
          disabled={loading || googleLoading}
          className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {googleLoading ? t('auth.signingUp') : t('auth.continueWithGoogle')}
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-sm text-gray-500">{t('auth.orContinueWithEmail')}</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-sm text-center">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.name')}
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
              placeholder={t('auth.namePlaceholder')}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.emailAddress')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
              placeholder={t('auth.emailPlaceholder')}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.password')}
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className="w-full px-4 py-3.5 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                placeholder={t('auth.passwordPlaceholder')}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-gray-500">{t('auth.passwordRequirements')}</p>
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg ${shadows.medium} text-sm font-medium text-white ${authColors.primaryButton} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('auth.signingUp')}
              </div>
            ) : (
              t('auth.signUp')
            )}
          </button>
        </div>

        {onSwitchToLogin && (
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t('auth.alreadyHaveAccount')}{' '}
              <button
                onClick={onSwitchToLogin}
                className={authColors.authLink}
              >
                {t('auth.signInLink')}
              </button>
            </p>
          </div>
        )}
      </form>
      
      {/* Legal notice - required for GDPR compliance */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-xs text-center text-gray-500">
          {t('auth.termsTextPrefix')}{' '}
          <Link to="/terms" className="text-blue-600 hover:text-blue-700 underline">
            {t('auth.termsLink')}
          </Link>
          {' '}{t('auth.termsTextAnd')}{' '}
          <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">
            {t('auth.privacyLink')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignUpForm;